import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, importProvidersFrom, isDevMode } from '@angular/core';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { connectAuthEmulator, getAuth, provideAuth } from '@angular/fire/auth';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireMessagingModule } from '@angular/fire/compat/messaging';
import { PerformanceMonitoringService } from '@angular/fire/compat/performance';
import { connectFirestoreEmulator, initializeFirestore, provideFirestore } from '@angular/fire/firestore';
import { provideAnimations } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, Router, provideRouter } from '@angular/router';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { TranslocoModule } from '@ngneat/transloco';
import { provideComponentStore } from '@ngrx/component-store';
import { provideEffects } from '@ngrx/effects';
import { provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { TraceService, createErrorHandler } from '@sentry/angular-ivy';
import { TUI_DIALOG_CLOSES_ON_BACK, TUI_IS_IOS } from '@taiga-ui/cdk';
import { TuiRootModule } from '@taiga-ui/core';
import { TUI_LANGUAGE, TUI_VIETNAMESE_LANGUAGE } from '@taiga-ui/i18n';
import { Environment, provideEnvironment } from '@us/app-config';
import { AreasEffects, areasFeature } from '@us/web/areas/data-access';
import {
    AuthEffects,
    AuthGuard,
    HttpConfigInterceptor,
    SetApiDomainInterceptor,
    ValidateShopIdGuard,
    authFeature
} from '@us/web/auth/data-access';
import { CartEffects, CartPageStore, cartFeature } from '@us/web/cart/data-access';
import { CategoriesStore } from '@us/web/categories/data-access';
import { DeliveryAddressEffects, deliveryAddressFeature } from '@us/web/delivery-addresses/data-access';
import { InventoryEffects, inventoryFeature } from '@us/web/inventory/data-access';
import { NotificationsEffects, notificationsFeature } from '@us/web/notifications/data-access';
import { OrdersEffects, ordersFeature } from '@us/web/orders/data-access';
import { PositionEffects, positionFeature } from '@us/web/position/data-access';
import { PreferenceEffects, preferenceFeature } from '@us/web/preferences/data-access';
import { PromotionsEffects, promotionsFeature } from '@us/web/promotions/data-access';
import {
    provideSanitizer,
    provideSvgSrcProcessor,
    provideTranslocoConfig,
    provideTranslocoLoader
} from '@us/web/shared/feature';
import { RouterUtil } from '@us/web/shared/utils';
import { ShopEffects, shopFeature } from '@us/web/shop/data-access';
import { ShopsEffects, shopsFeature } from '@us/web/shops/data-access';
import { socketIdInterceptor } from '@us/web/websockets/data-access';
import { vi as viConfig } from 'date-fns/locale';
import { DateFnsConfigurationService, DateFnsModule } from 'ngx-date-fns';
import { NgxGoogleAnalyticsModule } from 'ngx-google-analytics';
import { NgxMaskModule } from 'ngx-mask';
import { of } from 'rxjs';
import { environment } from '../environments/environment';
import { metaReducers } from '../meta-reducers';

const env: Environment = environment as Environment;

const vietnameseConfig = new DateFnsConfigurationService();
vietnameseConfig.setLocale(viConfig);

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideRouter([
            {
                path: RouterUtil.Cog.Unavailable,
                loadChildren: () => import('@us/web/unavailable/feature').then(m => m.routes)
            },
            {
                path: '',
                canActivate: [AuthGuard],
                providers: [
                    provideState(shopFeature),
                    provideState(shopsFeature),
                    provideState(inventoryFeature),
                    provideState(promotionsFeature),
                    provideState(cartFeature),
                    provideState(notificationsFeature),
                    provideState(areasFeature),
                    provideState(ordersFeature),
                    provideState(deliveryAddressFeature),
                    provideState(positionFeature),
                    provideEffects([
                        ShopEffects,
                        ShopsEffects,
                        InventoryEffects,
                        PromotionsEffects,
                        CartEffects,
                        NotificationsEffects,
                        AreasEffects,
                        OrdersEffects,
                        DeliveryAddressEffects,
                        PositionEffects
                    ]),
                    provideComponentStore(CartPageStore),
                    provideComponentStore(CategoriesStore)
                ],
                children: [
                    {
                        path: '',
                        canActivate: [ValidateShopIdGuard],
                        loadChildren: () => import('./pages/main/routes').then(m => m.routes)
                    }
                ]
            }
        ]),
        provideStore(
            {},
            {
                metaReducers,
                runtimeChecks: {
                    strictStateImmutability: true,
                    strictActionImmutability: true,
                    strictStateSerializability: true,
                    strictActionSerializability: false,
                    strictActionWithinNgZone: true,
                    strictActionTypeUniqueness: true
                }
            }
        ),
        provideState(authFeature),
        provideState(preferenceFeature),
        provideEffects([AuthEffects, PreferenceEffects]),
        provideStoreDevtools({
            maxAge: 25, // Retains last 25 states
            logOnly: !isDevMode(), // Restrict extension to log-only mode
            autoPause: true, // Pauses recording actions and state changes when the extension window is not open
            trace: isDevMode(), //  If set to true, will include stack trace for every dispatched action, so you can see it in trace tab jumping directly to that part of code
            traceLimit: 75 // maximum stack trace frames to be stored (in case trace option was provided as true)
        , connectInZone: true}),
        importProvidersFrom(
            IonicModule.forRoot({
                mode: 'md'
            }),
            TranslocoModule,
            DateFnsModule.forRoot(),
            NgxMaskModule.forRoot(),
            provideFirebaseApp(() => initializeApp(env.firebase)),
            // provideFirestore(() => getFirestore()),
            provideFirestore(() => {
                // long polling for Cypress
                const firestore = initializeFirestore(getApp(), {
                    experimentalForceLongPolling: true
                });
                if ((environment as Environment).useEmulators) {
                    connectFirestoreEmulator(firestore, 'localhost', 8080);
                }

                return firestore;
            }),
            provideAuth(() => {
                const auth = getAuth();
                if ((environment as Environment).useEmulators) {
                    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
                }
                auth.languageCode = 'vi';
                return auth;
            }),
            // provideMessaging(() => getMessaging())
            AngularFireModule.initializeApp(env.firebase),
            AngularFireMessagingModule,
            TuiRootModule,
            ...(environment.ga ? [NgxGoogleAnalyticsModule.forRoot(environment.ga)] : [])
        ),
        {
            provide: TUI_LANGUAGE,
            useValue: of(TUI_VIETNAMESE_LANGUAGE)
        },
        provideHttpClient(withInterceptors([socketIdInterceptor]), withInterceptorsFromDi()),
        { provide: HTTP_INTERCEPTORS, useClass: HttpConfigInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: SetApiDomainInterceptor, multi: true },
        { provide: DateFnsConfigurationService, useValue: vietnameseConfig },
        { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
        ...(env.production
            ? [
                {
                    provide: ErrorHandler,
                    useValue: createErrorHandler({
                        showDialog: false
                    })
                },
                {
                    provide: TraceService,
                    deps: [Router]
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: () => () => { },
                    deps: [TraceService],
                    multi: true
                },
                PerformanceMonitoringService
            ]
            : []),
        provideEnvironment(env),
        provideSvgSrcProcessor,
        provideSanitizer,
        provideTranslocoConfig(env.production),
        provideTranslocoLoader,
        {
            provide: TUI_IS_IOS,
            useValue: false
        },
        {
            provide: TUI_DIALOG_CLOSES_ON_BACK,
            useValue: of(true)
        }
    ]
};
